"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Calculator, Stethoscope, Scale, Wrench, ShoppingBag, Building2, Cpu, HardHat, ArrowRight, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const sectorsData = [{
  id: 1,
  title: "Accountants & Financial Services",
  description: "Target high-value local business clients and corporate accounts looking for reliable audit, tax, and advisory services.",
  impact: "High-intent lead generation",
  iconKey: "Calculator"
}, {
  id: 2,
  title: "Dentists & Private Healthcare",
  description: "Capture local patients searching for high-margin cosmetic dentistry, implants, orthodontic treatments, and private care.",
  impact: "Local map pack dominance",
  iconKey: "Stethoscope"
}, {
  id: 3,
  title: "Solicitors & Legal Practices",
  description: "Dominate search results for specialized legal areas including corporate law, conveyancing, personal injury, and family law.",
  impact: "Authoritative brand presence",
  iconKey: "Scale"
}, {
  id: 4,
  title: "Plumbers, Electricians & Trades",
  description: "Secure immediate, local emergency inquiries and ongoing residential or commercial installation contracts.",
  impact: "Hyper-local search coverage",
  iconKey: "Wrench"
}, {
  id: 5,
  title: "E-Commerce & Digital Retailers",
  description: "Scale organic revenue with technical SEO, schema structured data, product feed optimization, and category architecture.",
  impact: "Sustainable organic ROI",
  iconKey: "ShoppingBag"
}, {
  id: 6,
  title: "Real Estate & Estate Agencies",
  description: "Attract local property sellers, prospective landlords, and property investors in your specific geographic coverage zones.",
  impact: "High-ticket vendor inquiries",
  iconKey: "Building2"
}, {
  id: 7,
  title: "B2B SaaS & Tech Companies",
  description: "Drive qualified demo requests and free trial signups by targeting complex buyer personas across global search markets.",
  impact: "International pipeline growth",
  iconKey: "Cpu"
}, {
  id: 8,
  title: "Architecture & Construction",
  description: "Win lucrative commercial tenders, high-end residential commissions, and planning consultancies through organic search.",
  impact: "Premium project acquisition",
  iconKey: "HardHat"
}];
function SectorIcon({
  name
}: {
  name: string;
}) {
  const iconProps = {
    className: "size-6 text-primary"
  };
  switch (name) {
    case "Calculator":
      return <Calculator {...iconProps} />;
    case "Stethoscope":
      return <Stethoscope {...iconProps} />;
    case "Scale":
      return <Scale {...iconProps} />;
    case "Wrench":
      return <Wrench {...iconProps} />;
    case "ShoppingBag":
      return <ShoppingBag {...iconProps} />;
    case "Building2":
      return <Building2 {...iconProps} />;
    case "Cpu":
      return <Cpu {...iconProps} />;
    case "HardHat":
      return <HardHat {...iconProps} />;
    default:
      return <Building2 {...iconProps} />;
  }
}
export default function Sectors() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="industrysectorsgrid" data-nav="light" className="relative bg-background py-20 md:py-28 text-foreground overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
          <Badge variant="secondary" className="px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary bg-secondary/80 rounded-full">
            Tailored Industry Expertise
          </Badge>
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-foreground font-default">
            SEO Strategies Built for Your Specific Market
          </h2>
          <p className="text-muted-foreground text-base sm:text-lg font-default leading-relaxed">
            Search behavior varies dramatically across industries. I engineer bespoke, sector-specific SEO campaigns designed to capture real high-value commercial intent.
          </p>
        </div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.1
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {sectorsData.map((sector, i) => <motion.div key={sector.id} variants={itemVariants} data-index={i}>
              <Card className="h-full bg-card hover:bg-card/80 border border-border/80 shadow-sm hover:shadow-md transition-[background-color,border-color,box-shadow] duration-200 flex flex-col justify-between group rounded-xl">
                <div>
                  <CardHeader className="pt-6 pb-4">
                    <div className="size-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                      <SectorIcon name={sector.iconKey} />
                    </div>
                    <CardTitle className="text-lg font-semibold text-foreground font-default group-hover:text-primary transition-colors">
                      {sector.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="py-0">
                    <CardDescription className="text-muted-foreground text-sm font-default leading-relaxed mb-6">
                      {sector.description}
                    </CardDescription>
                  </CardContent>
                </div>
                
                <div className="px-6 pb-6 pt-2 border-t border-border/40 mt-auto">
                  <div className="flex items-center gap-2 text-xs font-medium text-foreground">
                    <CheckCircle2 className="size-3.5 text-accent shrink-0" />
                    <span>{sector.impact}</span>
                  </div>
                </div>
              </Card>
            </motion.div>)}
        </motion.div>

        <div className="mt-16 bg-muted/50 border border-border rounded-2xl p-8 sm:p-10 text-center max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="text-left space-y-2 max-w-xl">
            <h3 className="text-xl font-semibold text-foreground font-default">
              Don&apos;t see your specific industry listed?
            </h3>
            <p className="text-muted-foreground text-sm font-default">
              My foundational search methodologies adapt to any competitive business niche. Let&apos;s discuss your search growth opportunities directly.
            </p>
          </div>
          <Button asChild size="lg" className="shrink-0 bg-primary hover:bg-primary/90 text-primary-foreground font-medium rounded-lg">
            <Link to="/ContactPage" className="inline-flex items-center gap-2">
              Book a Free Consultation
              <ArrowRight className="size-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}